import { useState, useEffect } from 'react';

/* -------------------------------------------------------------------------- */
/*                                   CONFIG                                   */
/* -------------------------------------------------------------------------- */

// The submissions backend formats every date with date-fns 'P'/'Pp'/'p' tokens without passing
// a locale, so it always falls back to en-US: 'MM/dd/yyyy', 'MM/dd/yyyy, h:mm a' and 'h:mm a'.
// Results are delivered to the frontend as already-formatted strings, so the display format is
// changed by rewriting those strings rather than by re-formatting a raw date.

const STORAGE_KEY = 'qs.results.dateFormat';
const DEFAULT_FORMAT = 'US';

export const RESULTS_DATE_FORMAT_OPTIONS = [
  { value: 'US', label: 'MM/DD/YYYY' },
  { value: 'EU', label: 'DD/MM/YYYY' },
  { value: 'ISO', label: 'YYYY-MM-DD' }
];

const VALID_FORMATS = new Set(RESULTS_DATE_FORMAT_OPTIONS.map((option) => option.value));

/* -------------------------------------------------------------------------- */
/*                                 FORMATTING                                 */
/* -------------------------------------------------------------------------- */

// Leading 'MM/DD/YYYY' produced by date-fns 'P'/'Pp'
const US_DATE_REGEX = /^(\d{2})\/(\d{2})\/(\d{4})/;

const reorderDate = (format) => (match, month, day, year) =>
  format === 'ISO' ? `${year}-${month}-${day}` : `${day}/${month}/${year}`;

// Reorders the date part of a backend-formatted (en-US) string. Only the leading date is
// touched — the time is left exactly as the backend sends it, so every option behaves the
// same way. Anything without a leading en-US date (time-only values, values the backend
// couldn't parse) is passed through untouched.
export const formatResultsDate = (value, format) => {
  if (format === 'US' || typeof value !== 'string' || !value) return value;

  return value.replace(US_DATE_REGEX, reorderDate(format));
};

/* -------------------------------------------------------------------------- */
/*                                    STORE                                   */
/* -------------------------------------------------------------------------- */

// Preference is per browser only — deliberately not persisted server side.
const listeners = new Set();

export const getResultsDateFormat = () => {
  try {
    const format = window.localStorage.getItem(STORAGE_KEY);
    return VALID_FORMATS.has(format) ? format : DEFAULT_FORMAT;
  } catch (e) {
    return DEFAULT_FORMAT;
  }
};

export const setResultsDateFormat = (format) => {
  if (!VALID_FORMATS.has(format)) return;

  try {
    window.localStorage.setItem(STORAGE_KEY, format);
  } catch (e) {
    // Ignore write failures (private mode, quota) — the in-memory notification below still applies.
  }

  // 'storage' only fires in other tabs, so subscribers in this tab are notified directly.
  listeners.forEach((listener) => listener(format));
};

export const useResultsDateFormat = () => {
  const [format, setFormat] = useState(getResultsDateFormat);

  useEffect(() => {
    const listener = (nextFormat) => setFormat(nextFormat);
    const handleStorage = (event) => {
      if (event.key === STORAGE_KEY) setFormat(getResultsDateFormat());
    };

    listeners.add(listener);
    window.addEventListener('storage', handleStorage);

    return () => {
      listeners.delete(listener);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  return format;
};
